import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface GoogleSheetsColumnInfo {
  sheetId: string;
  spreadsheetId: string;
  sheetName: string;
  columns: string[];
  sampleRows?: Array<{
    rowNumber: number;
    values: string[];
  }>;
  linkedinColumn?: string; // Auto-mapped or user-selected
  domainColumn?: string;   // Auto-mapped or user-selected
  lastUpdated: string;
}

interface GoogleSheetsColumnsState {
  // Store multiple sheets' column data keyed by sheetId
  sheetsData: Record<string, GoogleSheetsColumnInfo>;
  // Currently active sheet for node configuration
  activeSheetId?: string;
  // Loading states
  isLoading: boolean;
  error?: string;
}

const initialState: GoogleSheetsColumnsState = {
  sheetsData: {},
  isLoading: false,
};

const googleSheetsColumnsSlice = createSlice({
  name: 'googleSheetsColumns',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = undefined;
      }
    },

    // Set error state
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Store column data for a sheet
    setSheetColumns: (state, action: PayloadAction<GoogleSheetsColumnInfo>) => {
      const sheetInfo = action.payload;
      const sheetId = `${sheetInfo.spreadsheetId}_${sheetInfo.sheetName}`;
      
      // Auto-map LinkedIn and Domain columns
      const linkedinColumn = sheetInfo.columns.find(col => 
        col.toLowerCase().includes('linkedin')
      );
      const domainColumn = sheetInfo.columns.find(col => 
        col.toLowerCase().includes('domain')
      );

      state.sheetsData[sheetId] = {
        ...sheetInfo,
        sheetId,
        linkedinColumn,
        domainColumn,
        lastUpdated: new Date().toISOString(),
      };
      
      state.activeSheetId = sheetId;
      state.isLoading = false;
      state.error = undefined;
    },

    // Update manual mapping for LinkedIn column
    setLinkedinMapping: (state, action: PayloadAction<{ sheetId: string; column: string }>) => {
      const { sheetId, column } = action.payload;
      if (state.sheetsData[sheetId]) {
        state.sheetsData[sheetId].linkedinColumn = column;
      }
    },

    // Update manual mapping for Domain column
    setDomainMapping: (state, action: PayloadAction<{ sheetId: string; column: string }>) => {
      const { sheetId, column } = action.payload;
      if (state.sheetsData[sheetId]) {
        state.sheetsData[sheetId].domainColumn = column;
      }
    },

    // Set active sheet for node configuration
    setActiveSheet: (state, action: PayloadAction<string>) => {
      state.activeSheetId = action.payload;
    },

    // Clear all data
    clearSheetsData: (state) => {
      state.sheetsData = {};
      state.activeSheetId = undefined;
      state.error = undefined;
    },

    // Remove specific sheet data
    removeSheetData: (state, action: PayloadAction<string>) => {
      const sheetId = action.payload;
      delete state.sheetsData[sheetId];
      
      if (state.activeSheetId === sheetId) {
        // Set new active sheet if available
        const remainingSheetIds = Object.keys(state.sheetsData);
        state.activeSheetId = remainingSheetIds.length > 0 ? remainingSheetIds[0] : undefined;
      }
    },
  },
});

export const {
  setLoading,
  setError,
  setSheetColumns,
  setLinkedinMapping,
  setDomainMapping,
  setActiveSheet,
  clearSheetsData,
  removeSheetData,
} = googleSheetsColumnsSlice.actions;

// Selectors
export const selectSheetsData = (state: any) => 
  state.googleSheetsColumns?.sheetsData || {};

export const selectActiveSheetData = (state: any) => {
  const googleSheetsState = state.googleSheetsColumns;
  if (!googleSheetsState) return undefined;
  
  const { activeSheetId, sheetsData } = googleSheetsState;
  return activeSheetId ? sheetsData[activeSheetId] : undefined;
};

export const selectSheetDataById = (state: any, sheetId: string) => 
  state.googleSheetsColumns?.sheetsData?.[sheetId];

export const selectIsLoading = (state: any) => 
  state.googleSheetsColumns?.isLoading || false;

export const selectError = (state: any) => 
  state.googleSheetsColumns?.error;

export const selectAvailableColumns = (state: any) => {
  const activeSheet = selectActiveSheetData(state);
  return activeSheet?.columns || [];
};

export const selectAvailableSampleRows = (state: any) => {
  const activeSheet = selectActiveSheetData(state);
  return activeSheet?.sampleRows || [];
};

export const selectLinkedinColumn = (state: any) => {
  const activeSheet = selectActiveSheetData(state);
  return activeSheet?.linkedinColumn;
};

export const selectDomainColumn = (state: any) => {
  const activeSheet = selectActiveSheetData(state);
  return activeSheet?.domainColumn;
};

export default googleSheetsColumnsSlice.reducer;
